import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../i18n';
import { KYCStatus, KYCDocument, KYCDocumentType, KYCStatusType } from '../types/rooms';
import { roomService } from '../services/roomService';

const DOC_TYPES: KYCDocumentType[] = ['business_license', 'trade_register', 'tax_certificate', 'other'];
const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export default function HotelKYCPage() {
  const { t } = useTranslation();
  const [kycStatus, setKycStatus] = useState<KYCStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<KYCDocumentType>('business_license');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await roomService.getKYCStatus();
      setKycStatus(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(()=>{
    let active=true,busy=false;
    const refresh=async()=>{if(busy||document.hidden)return;busy=true;try{const data=await roomService.getKYCStatus();if(active)setKycStatus(data)}catch{/* retain the last successful status while retrying */}finally{busy=false}};
    const timer=window.setInterval(refresh,15000);window.addEventListener('focus',refresh);
    return()=>{active=false;window.clearInterval(timer);window.removeEventListener('focus',refresh)};
  },[]);

  const docTypeLabel = (dt: KYCDocumentType): string => {
    const map: Record<KYCDocumentType, string> = {
      business_license: t('kyc.businessLicense'),
      trade_register: t('kyc.tradeRegister'),
      tax_certificate: t('kyc.taxCertificate'),
      other: t('kyc.other'),
    };
    return map[dt] || dt;
  };

  const statusLabel = (s: KYCStatusType): string => {
    const map: Record<KYCStatusType, string> = {
      not_submitted: t('kyc.notSubmitted'),
      pending: t('kyc.pending'),
      approved: t('kyc.approved'),
      rejected: t('kyc.rejected'),
    };
    return map[s] || s;
  };

  const statusColor = (s: KYCStatusType): string => {
    const map: Record<KYCStatusType, string> = {
      not_submitted: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (!ACCEPTED_MIME.includes(file.type)) {
      setUploadError('Format de fichier non supporté. Formats acceptés : PDF, PNG, JPG');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('La taille du fichier dépasse la limite de 10 Mo');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploading(true);
      await roomService.uploadKYCDocument(file, selectedDocType);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e: any) {
      setUploadError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitDossier = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await roomService.submitKYCDossier();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDoc = async (id: string) => {
    try {
      await roomService.deleteKYCDocument(id);
      setDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const currentStatus = kycStatus?.kyc_status || 'not_submitted';
  const documents = kycStatus?.documents || [];
  const canUpload = currentStatus === 'not_submitted' || currentStatus === 'rejected';
  const canSubmit = canUpload && documents.length > 0;

  if (loading) {
    return <div className="max-w-7xl mx-auto py-6 px-4 text-center text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('kyc.title')}</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={load} className="text-sm underline">{t('common.retry')}</button>
        </div>
      )}

      {/* KYC Status Banner */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('kyc.status')}</h2>
            <span className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${statusColor(currentStatus)}`}>
              {statusLabel(currentStatus)}
            </span>
          </div>
          {canSubmit && (
            <button onClick={handleSubmitDossier} disabled={submitting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition">
              {submitting ? t('common.loading') : t('kyc.submitDossier')}
            </button>
          )}
        </div>

        {/* Rejection reason */}
        {currentStatus === 'rejected' && kycStatus && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm font-medium text-red-800">{t('kyc.rejectionReason')}:</p>
            <p className="text-sm text-red-700 mt-1">
              {documents.find(d => d.rejection_reason)?.rejection_reason || '—'}
            </p>
          </div>
        )}
      </div>

      {/* Upload Section */}
      {canUpload && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('kyc.uploadDocument')}</h2>
          {uploadError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{uploadError}</div>
          )}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('kyc.documentType')}</label>
              <select value={selectedDocType} onChange={e => setSelectedDocType(e.target.value as KYCDocumentType)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500">
                {DOC_TYPES.map(dt => <option key={dt} value={dt}>{docTypeLabel(dt)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fichier (PDF, PNG, JPG — max 10 Mo)
              </label>
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileSelect} disabled={uploading}
                className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50" />
            </div>
            {uploading && <span className="text-sm text-gray-500">{t('common.loading')}</span>}
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
        </div>
        {documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{t('kyc.noDocuments')}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('kyc.documentType')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('rates.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{docTypeLabel(doc.document_type)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{doc.mime_type}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{formatFileSize(doc.file_size)}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                      doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>{doc.status}</span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    {canUpload && (
                      deleteConfirm === doc.id ? (
                        <>
                          <button onClick={() => handleDeleteDoc(doc.id)} className="text-red-600 hover:text-red-800 font-medium">{t('common.confirm')}</button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 hover:text-gray-700">{t('common.cancel')}</button>
                        </>
                      ) : (
                        <button onClick={() => setDeleteConfirm(doc.id)} className="text-red-500 hover:text-red-700">{t('rates.delete')}</button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
